/// Helpdesk widget bridge.
///
/// The lfh-web loader is fetched at runtime by `loadHelpdesk()`,
/// not from a `<script>` tag in index.html — third-party cookies
/// on the loader's origin would otherwise drop pre-consent and
/// fail ePrivacy. The loader installs a global Beacon-style
/// widget; we tell it who the player is via
/// `window.Beacon('identify', { idToken, name })` whenever the
/// Firebase ID token changes (initial session restore, sign-in,
/// sign-out reset to anonymous, ~hourly refresh).
///
/// `data-app-id="flatout-atc"` on the injected script matches the
/// iOS appId so support tickets land in the same app workspace.
///
/// The loader is async; if it hasn't initialised by the time the
/// first identify fires, retry briefly. Token-refresh events
/// (~once per hour) backstop us if the bounded retry runs out
/// before the script lands.

import { onIdTokenChanged } from 'firebase/auth'
import { firebaseAuth } from '../firebase/app'

const LOADER_URL =
  'https://lfh-web--liveflatouthelp.us-central1.hosted.app/loader.js'
let loaderInjected = false

/// Inject the Beacon loader `<script>` into the page. Idempotent —
/// safe to call repeatedly (e.g., on consent grant AND on-demand
/// from "Contact Support"). The script reads its `data-*` config
/// off `document.currentScript`, so the dataset attributes have to
/// be set on the element itself before the browser executes it.
export function loadHelpdesk(): void {
  if (loaderInjected) return
  if (typeof document === 'undefined') return
  loaderInjected = true
  const s = document.createElement('script')
  s.src = LOADER_URL
  s.async = true
  s.dataset.appId = 'flatout-atc'
  s.dataset.config = '{}'
  document.body.appendChild(s)
}

type BeaconFn = (action: string, ...args: unknown[]) => void

declare global {
  interface Window {
    Beacon?: BeaconFn
  }
}

interface IdentifyPayload {
  idToken: string
  name?: string
  isAnonymous?: boolean
}

const RETRY_INTERVAL_MS = 200
const RETRY_LIMIT = 25 // ~5 seconds

async function callIdentify(payload: IdentifyPayload): Promise<void> {
  for (let i = 0; i < RETRY_LIMIT; i++) {
    const beacon = window.Beacon
    if (typeof beacon === 'function') {
      beacon('identify', payload)
      return
    }
    await new Promise((r) => setTimeout(r, RETRY_INTERVAL_MS))
  }
  console.warn('Helpdesk Beacon never loaded; identify dropped')
}

/// Open the helpdesk widget programmatically (e.g., from a
/// "Contact Support" Settings row). Loads the script lazily on
/// first call so an "essential consent only" user who clicks
/// Contact Support still gets the widget — they're explicitly
/// asking for the third-party cookies that come with it. Falls
/// back to a mailto: link if the script never lands (loader
/// blocked, network down, ad blocker).
export async function openHelpdesk(): Promise<void> {
  loadHelpdesk()
  // Give the script a small window to materialise before falling
  // back. RETRY_LIMIT × RETRY_INTERVAL_MS bounds the total wait.
  for (let i = 0; i < RETRY_LIMIT; i++) {
    const beacon = window.Beacon
    if (typeof beacon === 'function') {
      beacon('open')
      return
    }
    await new Promise((r) => setTimeout(r, RETRY_INTERVAL_MS))
  }
  window.location.href = 'mailto:help@liveflatout.com'
}

/// Subscribe Firebase auth state to the helpdesk widget.
/// Returns the unsubscribe — call once at app boot.
export function subscribeHelpdeskIdentify(): () => void {
  return onIdTokenChanged(firebaseAuth, async (user) => {
    if (!user) return
    try {
      const idToken = await user.getIdToken()
      const name =
        user.displayName ??
        user.email ??
        (user.isAnonymous
          ? `Guest ${user.uid.slice(-4).toUpperCase()}`
          : undefined)
      await callIdentify({
        idToken,
        name,
        isAnonymous: user.isAnonymous,
      })
    } catch (err) {
      console.warn('Helpdesk identify failed', err)
    }
  })
}
